import React, { useEffect, useMemo, useRef, useState } from "react";

import {
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import {ExpandLess, ExpandMore } from "@mui/icons-material";
import Grid from "@mui/material/Grid2";

import { SearchBox, VariableCategoryCard } from "@/components/atoms";
import { Variable } from "@/types";
import { expandTypes, getTypeIcon } from "@/utils";

interface VariableDirectoryProps {
  variables: Variable[];
  onSelect: (variables: Variable[]) => void;
  onDelete: (variable: Variable) => void;
  selected: Set<number>;
  active: Set<number>;
}

const VariableDirectory: React.FC<VariableDirectoryProps> = ({
  variables,
  onSelect,
  onDelete,
  selected,
  active
}) => {

  const { menuConfig, directory } = useMemo(() => {
    const typeToGroupsMap: Record<string, Set<string>> = {};
    const cache: Record<string, Variable[]> = {};

    variables.forEach((v) => {
      const typeKey = v.tag_type;

      // Determine if this type actually uses submenus based on the user-defined whitelist
      const shouldHaveSubmenu = expandTypes.includes(typeKey);

      if (!typeToGroupsMap[typeKey]) {
        typeToGroupsMap[typeKey] = new Set();
      }

      if (shouldHaveSubmenu && v.tag_group) {
        typeToGroupsMap[typeKey].add(v.tag_group);

        // Key format for scoped subgroups: "Brands:Luxury"
        const compositeKey = `${typeKey}:${v.tag_group}`;
        if (!cache[compositeKey]) cache[compositeKey] = [];
        cache[compositeKey].push(v);
      }

      // Always bucket variables under their root Type key (e.g., "Brands" or "Demographics")
      // This serves as the default fallback when no subgroup is active
      if (!cache[typeKey]) cache[typeKey] = [];
      cache[typeKey].push(v);
    });

    // Generate menu layout configurations matching your explicit rules
    const menuConfig = Object.keys(typeToGroupsMap).map((type) => ({
      type,
      hasSubmenu: expandTypes.includes(type),
      groups: Array.from(typeToGroupsMap[type]),
    }));

    return { menuConfig, directory: cache };
  }, [variables, expandTypes]);

  const [selectedType, setSelectedType] = useState<string>("Interests");
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>({});
  const [searchText, setSearchText] = useState<string>("");
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (menuConfig.length > 0 && !selectedType) {
      setSelectedType(menuConfig[0].type);
    }
  }, [menuConfig, selectedType]);

  const handleTypeClick = (type: string, hasSubmenu: boolean) => {
    setSelectedType(type);
    setSelectedGroup(null); // Clear subgroup layout filters cleanly
    if (selectedType !== type) {
      setSearchText('');
    }

    if (hasSubmenu) {
      setOpenSubmenus(prev => ({ ...prev, [type]: !prev[type] }));
    }
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  const handleSubgroupClick = (type: string, group: string) => {
    setSelectedType(type);
    setSelectedGroup(group);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  };

  const filteredVariables = useMemo(() => {
    if (!selectedType) return {};

    const lookupKey = selectedGroup ? `${selectedType}:${selectedGroup}` : selectedType;
    const baseVariablesSet = directory[lookupKey] || [];
    const cleanQuery = searchText.toLowerCase().trim();

    const deck: Record<string, Variable[]> = {};

    baseVariablesSet.forEach((v) => {
      const matchesSearch = !cleanQuery ||
        v.tag_name.toLowerCase().includes(cleanQuery) ||
        v.tag_category.toLowerCase().includes(cleanQuery);

      if (matchesSearch) {
        if (!deck[v.tag_category]) deck[v.tag_category] = [];
        deck[v.tag_category].push(v);
      }
    });

    return deck;

  }, [selectedType, selectedGroup, directory, searchText])


  const handleSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSearchText(event.target.value || "");

  return (
    <>
      <Box mb={1}>
        <Typography variant="caption" >
          Navigate through the data sources in our taxonomy to discover the variables that are available, or use the search bar to find the ones you are looking for
        </Typography>
      </Box>
      <Grid container size={12} spacing={3} sx={{ height: 500 }}>

        {/* Type/Group Menu */}
        <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 3 }} sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Paper
            variant="outlined"
            sx={{
              flexGrow: 1,
              overflowY: "auto",
              backgroundColor: "transparent"
            }}>
            <List dense sx={{ p: 1 }}>
              {menuConfig.map((item) => {
                const isTypeActive = selectedType === item.type;
                const isMenuOpen = !!openSubmenus[item.type];

                return (
                  <Box key={item.type} sx={{ mb: 1 }}>
                    <ListItemButton
                      selected={isTypeActive && selectedGroup === null}
                      onClick={() => handleTypeClick(item.type, item.hasSubmenu)}
                      sx={{ borderRadius: '6px', py: 1 }}
                    >
                      <ListItemIcon sx={{ minWidth: 20, mr: 1 }}>
                        {getTypeIcon(item.type)}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.type}
                      />
                      {item.hasSubmenu && (isMenuOpen ? <ExpandLess fontSize="small"/> : <ExpandMore fontSize="small"/>)}
                    </ListItemButton>

                    {item.hasSubmenu && (
                      <Collapse in={isMenuOpen} timeout="auto" unmountOnExit>
                        <List disablePadding sx={{ pl: 1, mt: 1 }}>
                          {item.groups.map((group) => {
                            const isGroupActive = isTypeActive && selectedGroup === group;
                            return (
                              <ListItemButton
                                key={group}
                                selected={isGroupActive}
                                onClick={() => handleSubgroupClick(item.type, group)}
                                sx={{ borderRadius: '6px', py: 0.5 }}
                              >
                                <ListItemText
                                  primary={group}
                                  slotProps={{
                                    primary: {
                                      variant: "caption"
                                    }
                                  }}
                                />
                              </ListItemButton>
                            );
                          })}
                        </List>
                      </Collapse>
                    )}
                  </Box>
                );
              })}
            </List>
          </Paper>
        </Grid>
        <Grid size={{ xs: 8, sm: 8, md: 8, lg: 8, xl: 9 }} sx={{ height: '100%', display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
          <Box pb={2}>
            <SearchBox
              value={searchText}
              onSearch={handleSearchTextChange}
              fullWidth={true}
              placeholder="Search for variables or categories"
            />
          </Box>

          <Box ref={scrollRef} width="100%" sx={{ minHeight: 0, flexGrow: 1, overflowY: 'auto', pr: 1, scrollBehavior: 'smooth' }}>
            {Object.keys(filteredVariables).length > 0 ? (
              <Grid container spacing={2}>
                {Object.entries(filteredVariables).map(([category, itemsList]) => (
                  <Grid size={12} key={category}>
                    <VariableCategoryCard
                      category={category}
                      variables={itemsList}
                      onSelect={onSelect}
                      onDelete={onDelete}
                      selected={selected}
                      active={active}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', borderRadius: '6px', borderStyle: 'dashed', backgroundColor: 'transparent' }}>
                <Typography variant="body2" color="text.secondary">
                  No matching variables found.
                </Typography>
              </Paper>
            )}
          </Box>
        </Grid>
      </Grid>
    </>
  );
};

export default VariableDirectory;
